import React, { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SelectDivision from "./SelectDivision";
import { logRegistrationEvent } from "../utils/funnelAnalytics";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const AuthGate = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get("create") === "1" ? 1 : 0
  );

  useEffect(() => {
    if (searchParams.get("create") === "1") setActiveTab(1);
  }, [searchParams]);
  const returnTo = searchParams.get("return_to");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const { signIn, signUp, resetPasswordForEmail } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      await signIn(email, password);
      logRegistrationEvent("reg_account_sign_in", {});
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const emailRedirectTo = returnTo
        ? `${window.location.origin}/register?return_to=${encodeURIComponent(returnTo)}&auth=1&create=1`
        : `${window.location.origin}/register?auth=1&create=1`;

      await signUp(email, password, {
        userMetadata: { full_name: name },
        emailRedirectTo,
      });
      logRegistrationEvent("reg_account_sign_up", {});
      setMessage("If email confirmation is enabled, confirm your email to continue. We'll redirect you back automatically.");
    } catch (err) {
      setError(err.message || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!email?.trim()) {
      setError("Enter your email above, then tap send reset link.");
      return;
    }
    setLoading(true);
    try {
      await resetPasswordForEmail(email);
      setMessage("Check your email for a reset link. Open it on this device, then set a new password here.");
      setForgotMode(false);
    } catch (err) {
      setError(err.message || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 2,
        backgroundColor: "#fafafa",
      }}
    >
      <Box
        sx={{
          maxWidth: 420,
          width: "100%",
          backgroundColor: "#fff",
          borderRadius: 2,
          p: 4,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #eee",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#1a1a1a",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 3,
            textAlign: "center",
          }}
        >
          Register
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", textAlign: "center", mb: 3 }}>
          Sign in or create an account to register for the league (team or individual).
        </Typography>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            mb: 2,
            "& .MuiTab-root": { color: "#666", textTransform: "uppercase" },
            "& .Mui-selected": { color: "#000" },
            "& .MuiTabs-indicator": { backgroundColor: "#000" },
          }}
        >
          <Tab label="Sign in" />
          <Tab label="Create account" />
        </Tabs>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {activeTab === 0 && !forgotMode && (
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
              variant="outlined"
              size="small"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 1 }}
              variant="outlined"
              size="small"
            />
            <Typography variant="body2" sx={{ mb: 2, textAlign: "right" }}>
              <Button
                type="button"
                size="small"
                onClick={() => {
                  setForgotMode(true);
                  setError("");
                  setMessage("");
                }}
                sx={{ textTransform: "none", color: "#000", fontWeight: 600 }}
              >
                Forgot password?
              </Button>
            </Typography>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                fontWeight: 700,
                textTransform: "uppercase",
                py: 1.5,
                "&:hover": { backgroundColor: "#333" },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Sign in"}
            </Button>
          </form>
        )}
        {activeTab === 0 && forgotMode && (
          <form onSubmit={handleForgotPassword}>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              Enter the email for your account. We’ll send a link to reset your password.
            </Typography>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
              variant="outlined"
              size="small"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                fontWeight: 700,
                textTransform: "uppercase",
                py: 1.5,
                mb: 1,
                "&:hover": { backgroundColor: "#333" },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Send reset link"}
            </Button>
            <Button
              type="button"
              fullWidth
              variant="text"
              onClick={() => {
                setForgotMode(false);
                setError("");
              }}
              sx={{ color: "#666", textTransform: "none" }}
            >
              Back to sign in
            </Button>
          </form>
        )}
        {activeTab === 1 && (
          <form onSubmit={handleSignUp}>
            <TextField
              fullWidth
              label="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
              variant="outlined"
              size="small"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
              variant="outlined"
              size="small"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              helperText="At least 6 characters"
              sx={{ mb: 2 }}
              variant="outlined"
              size="small"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                fontWeight: 700,
                textTransform: "uppercase",
                py: 1.5,
                "&:hover": { backgroundColor: "#333" },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Create account"}
            </Button>
          </form>
        )}
      </Box>
    </Box>
  );
};

const BecomeAthlete = () => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("return_to");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const HERO_IMAGE = isMobile ? "/landing/mobile-hero.webp" : "/landing/splash1.webp";

  const scrollToProducts = () => {
    const el = document.getElementById("register-products");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8, backgroundColor: "#fafafa" }}>
        <CircularProgress sx={{ color: "#000" }} />
      </Box>
    );
  }

  if (user) {
    if (returnTo && returnTo.startsWith("/register/apply")) {
      return <Navigate to={returnTo} replace />;
    }
    return <Navigate to="/register/select-division" replace />;
  }

  // If we came from the registration flow (return_to is present), we must show the auth UI.
  // Otherwise unauthenticated users get redirected to product selection first.
  if (returnTo || searchParams.get("auth") === "1") {
    return <AuthGate />;
  }

  // Default: show a landing-page vibe, then let guests scroll into product selection.
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#000" }}>
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={HERO_IMAGE}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: isMobile ? "cover" : "contain",
            objectPosition: "center",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            px: 2,
            maxWidth: 720,
            transform: { xs: "translateY(-110px)", sm: "translateY(-180px)" },
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: 3,
              mb: 2,
              fontSize: { xs: "1.85rem", sm: "2.75rem", md: "3.25rem" },
              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
            }}
          >
            HADAF Sports League
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.95)",
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 4,
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
            }}
          >
            2 Cities
            <br />
            30+ Teams
            <br />
            300+ Athletes
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              onClick={scrollToProducts}
              variant="contained"
              disableElevation
              startIcon={<KeyboardArrowDownIcon />}
              sx={{
                backgroundColor: "rgba(255,255,255,0.92)",
                color: "#000",
                fontWeight: 900,
                borderRadius: 999,
                px: 3,
                py: 1.1,
                textTransform: "uppercase",
                letterSpacing: 1,
                "&:hover": { backgroundColor: "#fff" },
              }}
            >
              Scroll to select
            </Button>
          </Box>
        </Box>
      </Box>

      <Box id="register-products">
        <SelectDivision />
      </Box>
    </Box>
  );
};

export default BecomeAthlete;
